/**
 * Drawer (Round 1.3).
 *
 * Right-side slide-in panel with backdrop, escape-to-close, click-outside-to-
 * close, focus management, and ARIA dialog semantics. Used by all three
 * drilldowns (Approval / Execution / Tool detail).
 *
 * The visual styling already lives in `styles/index.css` as `.drawer-panel`
 * + `.drawer-head` + `.drawer-body` + `.drawer-stack` (lifted from the P29
 * wireframe). This component just adds the open/close state machine + the
 * backdrop + a11y plumbing.
 */
import { useCallback, useEffect, useRef, type JSX, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface DrawerProps {
  /** Open / closed state — caller controls. */
  readonly open: boolean;
  /** Called when the user dismisses (Esc, backdrop click, X button). */
  readonly onClose: () => void;
  /** Eyebrow text above the title (uppercase, small). */
  readonly eyebrow: string;
  /** Drawer title. */
  readonly title: string;
  /** Drawer body. */
  readonly children: ReactNode;
  /** Optional footer (action buttons, etc). */
  readonly footer?: ReactNode;
}

export function Drawer({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
}: DrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  // Esc to close
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    // Save focus, install Esc handler, focus the close button on mount.
    lastFocused.current = (document.activeElement as HTMLElement | null) ?? null;
    document.addEventListener('keydown', onKey);
    // Defer focus by a tick so the animation doesn't fight us.
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
      lastFocused.current?.focus?.();
    };
  }, [open, onKey]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={onClose} aria-hidden />
      {/* Panel */}
      <div
        ref={panelRef}
        className="drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        <div className="drawer-head">
          <div>
            <span>{eyebrow}</span>
            <h2 id="drawer-title">{title}</h2>
          </div>
          <button ref={closeBtnRef} type="button" onClick={onClose} aria-label="Close drawer">
            <X size={16} aria-hidden />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </div>
    </>
  );
}
